import { BookOutlined, PlusOutlined } from "@ant-design/icons"
import { Breadcrumb, Button, Layout, Table } from "antd"
import { AxiosError } from "axios"
import { LayoutPageContent, LayoutPageHeader } from "components"
import usePlatform from "contexts/platform/use-platform"
import { TErrorState } from "models/errors"
import { ICompany, IName } from "platform-client/types"
import React from "react"
import { useQuery } from "react-query"
import { RouteComponentProps, useHistory, withRouter } from "react-router"
import { Link } from "react-router-dom"
import { getUserTokenValue } from "utils/pocketknife/get-user-token-value"

const columns = (
  {
    onEditClick
  }: {
    onEditClick: (companyId: string) => void
  },
  userGroup: unknown
) => [
  {
    title: "Name",
    key: "name",
    sorter: (a: ICompany, b: ICompany) =>
      (a.name || "").localeCompare(b.name || ""),
    render: (c: { name: string; id: string }) => (
      <Link to={`/broker/${c.id}`}>
        <u>{c.name}</u>
      </Link>
    )
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status"
  },
  {
    title: "Contact name",
    dataIndex: "contactName",
    key: "contactName",
    render: (contactName: IName) => {
      return contactName && contactName.firstName
        ? `${contactName.firstName} ${contactName.lastName}`
        : "-"
    }
  },
  {
    title: "Contact email",
    dataIndex: "contactEmailAddress",
    key: "contactEmailAddress",
    render: (email: string) => {
      return email ? <a href={`mailto:${email}`}>{email}</a> : "-"
    }
  },
  {
    title: "Contact tel",
    dataIndex: "contactPhoneNumber",
    key: "contactPhoneNumber",
    render: (tel: string) => {
      return tel ? <a href={`tel:${tel}`}>{tel}</a> : "-"
    }
  },
  {
    title: "Actions",
    key: "actions",
    render: ({ id }: { id: string }) => {
      return (
        userGroup === "InsurerAdministrator" && (
          <Button onClick={() => onEditClick(id)}>Edit</Button>
        )
      )
    }
  }
]

const ListBrokers: React.FC<ListBrokersProps> = (props) => {
  const [apiError, setApiError] = React.useState<TErrorState>()
  const history = useHistory()
  const platform = usePlatform()
  const userGroupType = getUserTokenValue("grouptype", platform.token || "")

  const listBrokers = useQuery(
    ["listBrokers", "Broker"],
    () => platform.company.listCompanies("Broker"),
    {
      onError: (error: AxiosError) => {
        setApiError({
          errorNumber: error.code ? parseInt(error.code) : 500,
          validationMessage: "Unable to load brokers"
        })
      }
    }
  )

  return (
    <Layout>
      <LayoutPageHeader
        ghost={false}
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item key="/brokers">
              <BookOutlined />
              &nbsp;Brokers
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        title={"All Brokers"}
        extra={
          userGroupType === "InsurerAdministrator" && (
            <Link to={"/brokers/create"} className="ant-btn ant-btn-primary">
              <PlusOutlined /> Create broker
            </Link>
          )
        }
      />
      <LayoutPageContent error={apiError}>
        <Table
          rowKey="id"
          scroll={{ x: "max-content" }}
          loading={listBrokers.isLoading}
          columns={columns(
            {
              onEditClick: (companyId: string) =>
                history.push(`/brokers/edit/${companyId}`)
            },
            userGroupType
          )}
          dataSource={listBrokers.data}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ["10", "20"],
            showSizeChanger: true,
            hideOnSinglePage: false,
            position: ["bottomCenter"],
            size: "small"
          }}
        />
      </LayoutPageContent>
    </Layout>
  )
}

export type ListBrokersProps = RouteComponentProps<{
  type: string
}>

export default withRouter(ListBrokers)
